import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
}));

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				state[ticketIndex] = ticket;
				if (ticket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
			state[ticketIndex].internalUnread = 0;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const { ticket, message, currentUserId, listStatus } = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		let baseTicket;

		if (ticketIndex !== -1) {
			baseTicket = {
				...state[ticketIndex],
				...ticket,
			};
		} else {
			baseTicket = ticket;
		}

		// Locally maintain unread counters so we don't depend on backend counts.
		let internalUnread = baseTicket.internalUnread ?? 0;
		let unreadMessages = baseTicket.unreadMessages ?? 0;

		// Only count incoming messages (exclude own messages).
		if (!message?.fromMe) {
			if (baseTicket.isGroup) {
				// Groups: always track per-user unread, regardless of status.
				internalUnread = (internalUnread || 0) + 1;
			} else if (listStatus === "open") {
				// Non-group tickets in Working on: track internal unread.
				// Optionally require assignment to current user.
				if (!baseTicket.userId || baseTicket.userId === currentUserId) {
					internalUnread = (internalUnread || 0) + 1;
				}
			} else {
				// Other lists: preserve existing global unreadMessages behavior.
				unreadMessages = (unreadMessages || 0) + 1;
			}
		}

		const updatedTicket = {
			...baseTicket,
			internalUnread,
			unreadMessages,
		};

		if (ticketIndex !== -1) {
			state[ticketIndex] = updatedTicket;
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else {
			state.unshift(updatedTicket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_LAST_MESSAGE_ACK") {
		const { ticketId, lastMessageAck } = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1 && typeof lastMessageAck === "number") {
			state[ticketIndex] = { ...state[ticketIndex], lastMessageAck };
		}
		return [...state];
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		const ticketIndex = state.findIndex(t => t.contactId === contact.id);
		if (ticketIndex !== -1) {
			state[ticketIndex].contact = contact;
		}
		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}

	if (action.type === "UPDATE_INTERNAL_UNREAD") {
		const { ticketId, internalUnread } = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex] = {
				...state[ticketIndex],
				internalUnread,
			};
		}
		return [...state];
	}
};

	const TicketsList = (props) => {
		const { status, searchParam, showAll, selectedQueueIds, updateCount, style, onTicketAccepted } =
			props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const [orderStatusByPhone, setOrderStatusByPhone] = useState({});
	const { user } = useContext(AuthContext);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [status, searchParam, dispatch, showAll, selectedQueueIds]);

	const { tickets, hasMore, loading } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		queueIds: JSON.stringify(selectedQueueIds),
	});

	useEffect(() => {
		if (!status && !searchParam) return;
		dispatch({
			type: "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets]);

	const orderStatusCancelledRef = React.useRef(false);
	useEffect(() => {
		orderStatusCancelledRef.current = false;
		const phones = ticketsList
			.filter(t => t.contact?.number)
			.map(t => t.contact.number)
			.filter((p, i, arr) => arr.indexOf(p) === i)
			.slice(0, 30);
		if (phones.length === 0) {
			setOrderStatusByPhone({});
			return () => { orderStatusCancelledRef.current = true; };
		}
		api.get("api/allexpress/orders/statuses-by-phones", {
			params: { phones: phones.join(",") }
		})
			.then(({ data }) => {
				if (!orderStatusCancelledRef.current && data?.data) {
					setOrderStatusByPhone(data.data);
				}
			})
			.catch(() => {});
		return () => { orderStatusCancelledRef.current = true; };
	}, [ticketsList.length, ticketsList.map(t => t.contact?.number).filter(Boolean).join(",")]);

	useEffect(() => {
		const socket = openSocket();

		const shouldUpdateTicket = ticket => !searchParam &&
			// Always allow group tickets to be visible for users in the relevant queues
			(
				ticket.isGroup ||
				!ticket.userId ||
				ticket.userId === user?.id ||
				showAll
			) &&
			(!ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1);

		const notBelongsToUserQueues = ticket =>
			ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

		socket.on("connect", () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		});

		socket.on("ticket", data => {
			if (data.action === "updateUnread") {
				dispatch({
					type: "RESET_UNREAD",
					payload: data.ticketId,
				});
			}

			if (data.action === "updateInternalUnread") {
				// Only care about internal unread for working-on tickets (status === "open")
				if (status === "open" && data.userId === user?.id) {
					dispatch({
						type: "UPDATE_INTERNAL_UNREAD",
						payload: {
							ticketId: data.ticketId,
							internalUnread: data.internalUnread,
						},
					});
				}
			}

			// If ticket moved to another status (e.g. closed), remove from this list
			if (data.action === "update" && status && data.ticket.status !== status) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
				return;
			}

			if (data.action === "update" && shouldUpdateTicket(data.ticket)) {
				dispatch({
					type: "UPDATE_TICKET",
					payload: data.ticket,
				});
			}

			if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
			}
		});

		socket.on("appMessage", data => {
			if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
				const ticket = {
					...data.ticket,
					lastMessageFromMe: data.message?.fromMe ?? data.ticket.lastMessageFromMe,
					lastMessageAck: data.message?.ack ?? data.ticket.lastMessageAck ?? 0,
				};
				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: {
						ticket,
						message: data.message,
						currentUserId: user?.id,
						listStatus: status,
					},
				});
			}
			// When a message ack is updated (e.g. read receipt), refresh the tick in the ticket list
			if (data.action === "update" && data.message?.fromMe && data.message?.ticketId != null) {
				dispatch({
					type: "UPDATE_TICKET_LAST_MESSAGE_ACK",
					payload: {
						ticketId: Number(data.message.ticketId),
						lastMessageAck: data.message.ack != null ? Number(data.message.ack) : 0,
					},
				});
			}
		});

		socket.on("contact", data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [status, searchParam, showAll, user, selectedQueueIds]);

	useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{ticketsList.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							{ticketsList.map(ticket => {
								const phoneKey = (ticket.contact?.number || "").replace(/\D/g, "");
								return (
								<TicketListItem
									ticket={ticket}
									key={ticket.id}
									orderStatus={orderStatusByPhone[phoneKey]}
									onTicketAccepted={onTicketAccepted}
									onOpenTicket={ticketId => {
										// Locally reset internal unread when user opens a chat in Working on.
										if (status === "open") {
											dispatch({
												type: "UPDATE_INTERNAL_UNREAD",
												payload: {
													ticketId,
													internalUnread: 0,
												},
											});
										}
									}}
								/>
							);})}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;
